import { Fragment, useEffect, useRef } from 'react';
import { comicPanelBoxes, panelCaptions, COMIC_PANELS, type AuthorSeat, type ComicDef, type PublicRound } from '../../shared/types';
import { tint } from '../lib/tint';

/**
 * The Bildaufgabe board (2026-09-08).
 *
 * A comic is uploaded as one sheet, in whatever grid its source has (the
 * Vater-und-Sohn strips come both as 2×3 portrait and 3×2 landscape). The
 * board re-tiles it into the canonical **two columns, three rows** — that
 * re-tiling *is* the "standard format", done at render time via background
 * cropping, so the uploaded file is never re-encoded and the moderator never
 * has to prepare anything.
 *
 * Each panel gets its caption slot on the outside of the sheet: panels in
 * the left column are captioned to their left, panels in the right column to
 * their right, which is exactly the order the authors write in —
 *
 *     [ caption 1 ][ panel 1 ][ panel 2 ][ caption 2 ]
 *     [ caption 3 ][ panel 3 ][ panel 4 ][ caption 4 ]
 *     [ caption 5 ][ panel 5 ][ panel 6 ][ caption 6 ]
 *
 * — one panel per turn, alternating between the two authors.
 *
 * `layout="below"` (2026-09-09) puts each caption *under* its panel instead.
 * Four columns need a wide frame; in the 9:16 clip they would squeeze the
 * panels down to a third of the frame and leave the rest of it empty, so a
 * portrait frame stacks caption under panel and keeps the same reading
 * order 1 … 6.
 */
export function ComicBoard(props: {
  comic: ComicDef;
  src: string;
  round: PublicRound;
  authors: AuthorSeat[];
  /** Hide the caption slots — used by the intro slide and small previews. */
  captions?: boolean;
  /** Blink the caret in the panel currently being captioned. */
  showCaret?: boolean;
  /** Where a caption sits: beside its panel (wide frames) or under it (9:16). */
  layout?: 'outside' | 'below';
  className?: string;
}) {
  const { comic, src, round, authors, captions = true, showCaret = false, layout = 'outside', className } = props;
  const boxes = comicPanelBoxes(comic);
  const texts = panelCaptions(round);
  const live = round.phase === 'running' || round.phase === 'paused';
  const current = Math.min(round.panelStarts.length - 1, COMIC_PANELS - 1);
  const activeSeat = authors[round.activeAuthor];
  const liveRef = useRef<HTMLDivElement>(null);

  // Keep the panel being captioned in view on smaller author screens.
  useEffect(() => {
    if (!showCaret) return;
    liveRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [current, showCaret]);

  // A source panel is (1/cols × 1/rows) of the sheet; background-size blows
  // the sheet up so exactly that fraction fills the cell.
  const panelAspect = comic.width / comic.cols / (comic.height / comic.rows);

  return (
    <div
      className={`comicboard comicboard--${layout} ${captions ? '' : 'comicboard--bare'} ${className ?? ''}`}
      style={{ ['--panel-aspect' as string]: String(panelAspect) }}
      data-testid="comic-board"
    >
      {boxes.map((box, i) => {
        const displayRow = Math.floor(i / 2) + 1; // canonical 2×3, whatever the source grid is
        const leftColumn = i % 2 === 0;
        // Beside the panel (columns 1 and 4 of four) or under it (two columns,
        // panel and caption rows alternating).
        const panelCell = layout === 'below'
          ? { gridColumn: leftColumn ? 1 : 2, gridRow: displayRow * 2 - 1 }
          : { gridColumn: leftColumn ? 2 : 3, gridRow: displayRow };
        const captionCell = layout === 'below'
          ? { gridColumn: leftColumn ? 1 : 2, gridRow: displayRow * 2 }
          : { gridColumn: leftColumn ? 1 : 4, gridRow: displayRow };
        const isLive = captions && showCaret && live && i === current;
        const written = round.panelStarts[i] !== undefined;
        const seat = captionSeat(round, authors, i, current, live);
        // Panel and its caption are emitted as a pair, so a narrow screen
        // that falls back to auto placement still reads 1, 2, 3, … 6.
        return (
          <Fragment key={i}>
            <div
              className={`comicpanel ${isLive ? 'comicpanel--live' : ''}`}
              style={{
                ...panelCell,
                backgroundImage: `url("${src}")`,
                backgroundSize: `${comic.cols * 100}% ${comic.rows * 100}%`,
                backgroundPosition: `${comic.cols === 1 ? 0 : (box.col / (comic.cols - 1)) * 100}% ${
                  comic.rows === 1 ? 0 : (box.row / (comic.rows - 1)) * 100
                }%`,
                ...(isLive && activeSeat ? { borderColor: activeSeat.color } : {}),
              }}
              data-testid={`comic-panel-${i}`}
            >
              <span className="comicpanel__no">{i + 1}</span>
            </div>
            {captions && (
              <div
                ref={isLive ? liveRef : undefined}
                className={`comiccap comiccap--${leftColumn ? 'left' : 'right'} ${isLive ? 'comiccap--live' : ''} ${
                  written ? '' : 'comiccap--pending'
                }`}
                style={{
                  ...captionCell,
                  ...(seat ? { background: tint(seat.color, 0.16), borderColor: seat.color } : {}),
                }}
                data-testid={`comic-caption-${i}`}
              >
                <span className="comiccap__no">{i + 1}</span>
                <span className="comiccap__text story">
                  {texts[i]}
                  {isLive && (
                    <span
                      className="story__caret"
                      style={{ background: activeSeat?.color ?? '#fff7ec' }}
                      aria-hidden="true"
                    />
                  )}
                </span>
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * Who owns a panel's caption: the author whose segment covers its first
 * character — falling back to the seat currently at the keyboard for the
 * panel being written, which has no characters yet.
 */
function captionSeat(
  round: PublicRound,
  authors: AuthorSeat[],
  panel: number,
  current: number,
  live: boolean,
): AuthorSeat | undefined {
  const start = round.panelStarts[panel];
  if (start === undefined) return undefined;
  if (live && panel === current && start === round.text.length) return authors[round.activeAuthor];
  let author: number | undefined;
  for (const s of round.segments) {
    if (s.start <= start) author = s.author;
    else break;
  }
  return author === undefined ? undefined : authors[author];
}

/** The comic URL for a session — the bytes are served over plain HTTP. */
export function comicSrc(pin: string, comicId: string): string {
  return `/api/sessions/${pin}/comics/${encodeURIComponent(comicId)}`;
}
